import { useState, useEffect, useCallback } from 'react';
import { getSubscriptionStatus, SubscriptionStatus } from '../services/subscriptionService';

interface UseSubscriptionReturn {
    hasActiveSubscription: boolean;
    subscriptionStatus: SubscriptionStatus | null;
    loading: boolean;
    isModalOpen: boolean;
    openSubscriptionModal: () => void;
    closeSubscriptionModal: () => void;
    refreshSubscriptionStatus: () => Promise<void>;
}

export const useSubscription = (): UseSubscriptionReturn => {
    const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchSubscriptionStatus = useCallback(async () => {
        try {
            setLoading(true);
            const status = await getSubscriptionStatus();
            setSubscriptionStatus(status);
        } catch (error) {
            console.error('Error fetching subscription status:', error);
            // On error, assume free tier - don't block features
            // This ensures that if the subscription API fails, users can still use free features
            setSubscriptionStatus({
                hasSubscription: false,
                status: 'free',
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSubscriptionStatus();
    }, [fetchSubscriptionStatus]);

    const hasActiveSubscription =
        subscriptionStatus?.hasSubscription === true &&
        subscriptionStatus?.status === 'active';

    const openSubscriptionModal = useCallback(() => {
        setIsModalOpen(true);
    }, []);

    const closeSubscriptionModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const refreshSubscriptionStatus = useCallback(async () => {
        await fetchSubscriptionStatus();
    }, [fetchSubscriptionStatus]);

    return {
        hasActiveSubscription,
        subscriptionStatus,
        loading,
        isModalOpen,
        openSubscriptionModal,
        closeSubscriptionModal,
        refreshSubscriptionStatus,
    };
};
